import React, { useState, Fragment, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import CloseIcon from "@mui/icons-material/Close";
import Toast from "../../../Components/Toast/Toast";
import Spinner from "react-bootstrap/Spinner";
import toArrayBuffer from "to-array-buffer";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/fontawesome-free-solid";

export default function AddSurgery({ onSurgeryAdded }) {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [isLoading, setIsLoading] = useState(false);
  const [ToastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState("");
  const [ToastIcon, setToastIcon] = useState(faTimesCircle);
  const [iconColor, setIconColor] = useState("");
  const [procedureImageFile, setProcedureImageFile] = useState(null);
  const [uploadingProcedureImage, setUploadingProcedureImage] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [doctorsPerPage] = useState(10); // Show 10 doctors per page
  const [searchTerm, setSearchTerm] = useState("");
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null); // For cursor-based pagination
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true); // Flag to check if more doctors available
  const [isLoadingMore, setIsLoadingMore] = useState(false); // For loading more doctors

  // Helper to coerce various truthy/falsey PMDC values into boolean
  const parseBool = (v) => {
    return v === true || v === 'true' || v === 1 || v === '1';
  };

  // Define fetchDoctors outside useEffect so it can be called from loadMoreDoctors
  const fetchDoctors = async (cursor = null) => {
    if (cursor) {
      setIsLoadingMore(true);
    } else {
      setLoadingDoctors(true);
    }
    
    // Declare variables at function scope so they can be accessed in finally block
    let hasMore = false;
    let doctorList = [];
    
    try {
      // Build API URL with cursor as query parameter if provided
      let apiUrl = API_CONSTANTS.DOCTOR_LIST_SEARCH();
      let requestPayload = {};
      
      if (cursor) {
        // Add cursor as query parameter
        apiUrl = `${API_CONSTANTS.DOCTOR_LIST_SEARCH()}?last=${encodeURIComponent(cursor)}`;
      }


      const response = await axios.post(
        apiUrl,
        requestPayload,
        {
          headers: API_CONSTANTS.headers(),
        }
      );


      // Detailed response structure: (logs removed)
      
      // Parse response - API returns data in response.response.Items (based on backend structure)
      let lastKey = null;
      
      // Handle response.response.Items structure
      if (response.data?.response?.Items && Array.isArray(response.data.response.Items)) {
        doctorList = response.data.response.Items;
        
        // Check for LastEvaluatedKey to determine if there are more doctors
          if (response.data.response?.LastEvaluatedKey) {
          lastKey = response.data.response.LastEvaluatedKey;
          hasMore = true;
        } else {
          hasMore = false;
        }
      } 
      // Handle direct response.Items structure
      else if (response.data?.Items && Array.isArray(response.data.Items)) {
        doctorList = response.data.Items;
        
        if (response.data?.LastEvaluatedKey) {
          lastKey = response.data.LastEvaluatedKey;
          hasMore = true;
        } else {
          hasMore = false;
        }
      } 
      // Handle array response
      else if (Array.isArray(response.data)) {
        doctorList = response.data;
        hasMore = false;
      } 
      else {
        // Unexpected response structure (logs removed)
        doctorList = [];
        hasMore = false;
      }
      
      // Summary: total doctors in this batch available (logs removed)
      
      // If loading more, append to existing list; otherwise, set as initial list
      if (cursor) {
        setDoctorsList(prevList => {
          const newList = [...prevList, ...doctorList];

          // Check for duplicates 
          const uniqueIds = new Set(newList.map(d => d.Id));
          if (uniqueIds.size !== newList.length) {
          }

          return newList;
        });
      } else {
        setDoctorsList(doctorList);
        setCurrentPage(1);
        setSearchTerm("");
      }
      
      setLastEvaluatedKey(lastKey);
      setHasMoreDoctors(hasMore);
      
      if (doctorList.length > 0) {
        // Do not show success toast for doctor loading (logs/toasts removed)
      } else if (!cursor) {
        handleToast("No doctors found", faTimesCircle, "#dc3545", "show-error");
      }
    } catch (error) {
      handleToast("Error loading doctors: " + error.message, faTimesCircle, "#dc3545", "show-error");

      if (!cursor) {
        setDoctorsList([]);
      }
      setHasMoreDoctors(false);
    } finally {
      if (cursor) {
        setIsLoadingMore(false);
      } else {
        setLoadingDoctors(false);
      }
    }
  };

  // useEffect to fetch doctors when modal is opened
  useEffect(() => {
    if (show) {
      fetchDoctors();
    }
  }, [show]);

  // Function to load more doctors using cursor-based pagination
  const loadMoreDoctors = async () => {
    if (!lastEvaluatedKey) {
      handleToast("No more doctors to load", faTimesCircle, "#dc3545", "show-error");
      return;
    }

    if (!hasMoreDoctors) {
      handleToast("All doctors have been loaded", faTimesCircle, "#dc3545", "show-error");
      return;
    }

    // Loading more doctors with cursor
    
    try {
      // Send the lastEvaluatedKey directly to fetchDoctors
      await fetchDoctors(lastEvaluatedKey);
    } catch (error) {
      handleToast("Failed to load more doctors", faTimesCircle, "#dc3545", "show-error");
    }
  };

  const handleDoctorSelect = async (doctor, setFieldValue) => {
    if (!doctor) {
      return;
    }

    // Handle different ID field names
    const doctorId = doctor.Id || doctor.id || doctor.doctor_id;

    // Check if doctor is already selected
    const isAlreadySelected = selectedDoctors.some(d => d.doctor_id === doctorId);
    if (isAlreadySelected) {
      handleToast("Doctor already selected", faTimesCircle, "#dc3545", "show-error");
      return;
    }

    try {
      // Fetching doctor details
      const response = await axios.get(
        `${API_CONSTANTS.BASEURL}admin/doctors/${doctorId}`,
        {
          headers: API_CONSTANTS.headers(),
        }
      );
      const doctorData = response.data.response;

      const newDoctor = {
        doctor_id: doctorId,
        doctor_name: doctorData.Fname || `${doctorData.personal_info?.firstName || ''} ${doctorData.personal_info?.lastName || ''}`.trim(),
        doctor_specialization: doctorData.specialization || "",
        doctor_image_url: doctorData.personal_info?.profileUrl || "",
        rating: doctorData.rating?.averageRating?.toString() || "",
        experience: doctorData.yearsOfExperience || "",
        pmdc: parseBool(doctorData.pmdc),
      };

      const updatedDoctors = [...selectedDoctors, newDoctor];
      setSelectedDoctors(updatedDoctors);
      setFieldValue("doctors", updatedDoctors);
      // Doctor added successfully
    } catch (error) {
      // Error loading doctor details
      handleToast("Error loading doctor details: " + error.message, faTimesCircle, "#dc3545", "show-error");
    }
  };

  const handleRemoveDoctor = (doctorId, setFieldValue) => {
    const updatedDoctors = selectedDoctors.filter(d => d.doctor_id !== doctorId);
    setSelectedDoctors(updatedDoctors);
    setFieldValue("doctors", updatedDoctors);
  };

  // Pagination helper functions
  const getFilteredAndPaginatedDoctors = () => {
    // Filter doctors based on search term
    const filteredDoctors = doctorsList.filter(doctor => {
      const doctorName = (doctor.Fname || doctor.firstName || doctor.first_name || doctor.name || 'Unknown').toLowerCase();
      const doctorEmail = (doctor.email || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      return doctorName.includes(searchLower) || doctorEmail.includes(searchLower);
    });

    // Filter out already selected doctors
    const availableDoctors = filteredDoctors.filter(doctor => 
      !selectedDoctors.some(selected => {
        const doctorId = doctor.Id || doctor.id || doctor.doctor_id;
        return selected.doctor_id === doctorId;
      })
    );

    // Calculate pagination
    const totalPages = Math.ceil(availableDoctors.length / doctorsPerPage);
    const startIdx = (currentPage - 1) * doctorsPerPage;
    const endIdx = startIdx + doctorsPerPage;
    const paginatedDoctors = availableDoctors.slice(startIdx, endIdx);

    // Pagination diagnostics removed

    return {
      paginatedDoctors,
      totalPages,
      totalAvailable: availableDoctors.length,
      currentPage,
    };
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = (totalPages) => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleToast = (message, icon, iconColor, className) => {
    setToastIcon(icon);
    setToastMessage(message);
    setShowToast(`show ${className}`);
    setIconColor(iconColor);
    setTimeout(() => setShowToast(""), 2900);
  };

  const uploadImage = async (file) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        const fileRes = btoa(reader.result);
        const testImg = toArrayBuffer(fileRes);
        const data = testImg;
        try {
          const response = await axios.post(
            `${API_CONSTANTS.BASEURL}admin/image-upload-api/user/a80422ab-1f3c-4676-8200-fc38a156599f/type/new`,
            data,
            {
              headers: API_CONSTANTS.imgheaders(),
            }
          );
          resolve(response.data.response.url);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("File reading error"));
    });
  };

  const handleProcedureImageChange = async (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setProcedureImageFile(file);
      setUploadingProcedureImage(true);
      try {
        const url = await uploadImage(file);
        setFieldValue("procedure_image_url", url);
        handleToast("Procedure image uploaded successfully", faCheckCircle, "#009b94", "show-success");
      } catch (error) {
        handleToast("Error uploading procedure image", faTimesCircle, "#dc3545", "show-error");
      } finally {
        setUploadingProcedureImage(false);
      }
    }
  };


  const initialValues = {
    procedure_name: "",
    procedure_type: "surgery",
    description: "",
    procedure_image_url: "",
    doctors: [], // Array of selected doctors
    clinic_name: "",
    clinic_address: "",
    city: "",
    duration: "",
    pricing: "",
    currency: "PKR",
  };

  const validationSchema = Yup.object({
    procedure_name: Yup.string()
      .required("Procedure name is required")
      .min(3, "Procedure name must be at least 3 characters"),
    procedure_type: Yup.string()
      .required("Procedure type is required")
      .oneOf(["surgery", "procedure"], "Invalid procedure type"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters"),
    procedure_image_url: Yup.string()
      .required("Procedure image URL is required")
      .url("Invalid URL format"),
    doctors: Yup.array()
      .min(1, "At least one doctor must be selected")
      .required("Please select at least one doctor"),
    clinic_name: Yup.string()
      .required("Clinic name is required")
      .min(3, "Clinic name must be at least 3 characters"),
    clinic_address: Yup.string()
      .required("Clinic address is required")
      .min(10, "Clinic address must be at least 10 characters"),
    city: Yup.string()
      .required("City is required")
      .min(2, "City must be at least 2 characters"),
    duration: Yup.string()
      .required("Duration is required")
      .min(1, "Duration must be specified"),
    pricing: Yup.number()
      .required("Pricing is required")
      .positive("Pricing must be a positive number"),
    currency: Yup.string()
      .required("Currency is required")
      .oneOf(["PKR", "USD", "EUR"], "Invalid currency"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldValue }) => {
    setIsLoading(true);

    // Check if doctors array has at least one doctor
    if (!values.doctors || values.doctors.length === 0) {
      handleToast("Please select at least one doctor", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    // Validate all required fields BEFORE trim
    if (!values.clinic_name || values.clinic_name.trim() === "") {
      handleToast("Clinic name is required and cannot be empty", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    // For now, use the first selected doctor for API compatibility
    const primaryDoctor = values.doctors[0];

    if (!primaryDoctor) {
      handleToast("Primary doctor information is missing", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    // Validate all required fields before submitting
    const clinicAddress = values.clinic_address?.trim();
    const clinicName = values.clinic_name?.trim();
    const city = values.city?.trim();
    
    if (!clinicAddress) {
      handleToast("Clinic address is required", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    if (!clinicName) {
      handleToast("Clinic name is required", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    if (!city) {
      handleToast("City is required", faTimesCircle, "#dc3545", "show-error");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    const submitData = {
      procedure_name: values.procedure_name?.trim(),
      procedure_type: values.procedure_type,
      description: values.description?.trim(),
      procedure_image_url: values.procedure_image_url || "https://via.placeholder.com/300x200.png?text=Procedure+Image",
      duration: values.duration?.trim(),
      // Send full doctor details for each doctor WITH all required fields
      doctors: values.doctors.map(doctor => ({
        doctor_id: doctor.doctor_id,
        doctor_name: doctor.doctor_name?.trim(),
        doctor_specialization: doctor.doctor_specialization?.trim(),
        doctor_image_url: doctor.doctor_image_url || "https://via.placeholder.com/150x150.png?text=No+Image",
        pmdc: parseBool(doctor.pmdc),
        clinic_name: clinicName, // Move inside doctor
        clinic_address: clinicAddress, // Move inside doctor
        city: city, // Move inside doctor
        pricing: values.pricing ? String(values.pricing) : "", // Pricing per doctor
        currency: values.currency, // Currency per doctor
        experience: parseInt(doctor.experience) || 0, // Experience per doctor
      })),
    };

    const headers = API_CONSTANTS.headers();

    axios
      .post(`${API_CONSTANTS.ADD_PROCEDURE_AND_SURGERY()}`, submitData, {
        headers: headers,
      })
      .then((response) => {
        if (response.data?.response?.message === "Procedure/Surgery added successfully" || 
            response.data?.message === "Success" || 
            response.data?.message === "Procedure/Surgery added successfully") {
          handleToast(
            "Procedure/Surgery Added Successfully",
            faCheckCircle,
            "#009b94",
            "show-success"
          );
          handleClose();
          resetForm();
          setProcedureImageFile(null);
          setSelectedDoctors([]); 
          setFieldValue("doctors", []);
          if (onSurgeryAdded) {
            onSurgeryAdded();
          }
        } else {
          handleToast(
            "Unexpected response from server",
            faTimesCircle,
            "#dc3545",
            "show-error"
          );
        }
      })
      .catch((error) => {
        // Build comprehensive error message
        let errorMessage = error.response?.data?.message || error.response?.data?.response?.message || error.message || "Error adding procedure/surgery";
        
        handleToast(
          errorMessage,
          faTimesCircle,
          "#dc3545",
          "show-error"
        );
      })
      .finally(() => {
        setIsLoading(false);
        setSubmitting(false);
      });
  };

  return (
    <Fragment>
      <button
        className="btn"
        onClick={handleShow}
        style={{
          background: "#009B93",
          color: "#fff",
          padding: "10px 18px",
          borderRadius: "4px",
          border: "none",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
        Add New Procedure/Surgery
      </button>
      <Modal show={show} size="lg">
        <Modal.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between">
            <h4>Add Procedure/Surgery</h4>
            <CloseIcon
              onClick={handleClose}
              style={{ cursor: "pointer" }}
            />
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form className="form-inline row g-3">
              <div className="col-md-6 mb-4">
                <Field
                  type="text"
                  name="procedure_name"
                  placeholder="Procedure Name"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage name="procedure_name" component="div" className="error-message" />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  as="select"
                  name="procedure_type"
                  className="form-control border-0 border-bottom"
                >
                  <option value="surgery">Surgery</option>
                  <option value="procedure">Procedure</option>
                </Field>
                <ErrorMessage name="procedure_type" component="div" className="error-message" />
              </div>
              <div className="col-md-12 mb-4">
                <Field
                  as="textarea"
                  name="description"
                  placeholder="Description"
                  className="form-control border-0 border-bottom"
                  rows="4"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-12 mb-4">
                <label htmlFor="procedure_image" className="form-label">Procedure Image</label>
                <input
                  type="file"
                  id="procedure_image"
                  accept="image/*"
                  onChange={(e) => handleProcedureImageChange(e, setFieldValue)}
                  className="form-control"
                  disabled={uploadingProcedureImage}
                />
                {uploadingProcedureImage && <div>Uploading...</div>}
                <Field
                  type="url"
                  name="procedure_image_url"
                  placeholder="Procedure Image URL"
                  className="form-control border-0 border-bottom mt-2"
                  readOnly
                />
                <ErrorMessage
                  name="procedure_image_url"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-12 mb-4">
                <label className="form-label">Select Doctors</label>
                {loadingDoctors && (
                  <div className="alert alert-info mb-3" style={{ padding: '8px 12px' }}>
                    <strong> Loading doctors...</strong> 
                  </div>
                )}
                {!loadingDoctors && doctorsList.length === 0 && (
                  <div className="alert alert-warning mb-3" style={{ padding: '8px 12px' }}>
                    <strong> No doctors found</strong> 
                    <br />
                    <small>Verify doctors exist in the database</small>
                  </div>
                )}
                {/* doctors available indicator removed */}

                {/* SEARCH BOX */}
                {!loadingDoctors && doctorsList.length > 0 && (
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder=" Search doctors by name..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page on search
                      }}
                      className="form-control border-0 border-bottom"
                      style={{ marginBottom: '10px' }}
                    />
                  </div>
                )}

                {/* PAGINATION DOCTOR LIST */}
                {!loadingDoctors && doctorsList.length > 0 && (
                  (() => {
                    const { paginatedDoctors, totalPages, totalAvailable } = getFilteredAndPaginatedDoctors();
                    
                    return (
                      <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '0',
                        backgroundColor: '#f9f9f9',
                        marginBottom: '15px'
                      }}>
                        {/* DOCTOR LIST - SCROLLABLE */}
                        <div style={{
                          maxHeight: '350px',
                          overflowY: 'auto',
                          paddingRight: '8px',
                          paddingLeft: '12px'
                        }} className="doctor-list-scroll">
                          {paginatedDoctors.length > 0 ? (
                            paginatedDoctors.map((doctor) => {
                              const doctorId = doctor.Id || doctor.id || doctor.doctor_id;
                              const doctorName = doctor.Fname || doctor.firstName || doctor.first_name || doctor.name || 'Unknown Doctor';
                              const specialization = doctor.specialization || 'No specialization';

                              return (
                                <div
                                  key={doctorId}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 8px',
                                    borderBottom: '1px solid #e0e0e0',
                                    backgroundColor: '#fff',
                                    marginBottom: '4px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e8f5e9';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <strong style={{ color: '#009b94', fontSize: '14px' }}>{doctorName}</strong>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                      {specialization}
                                    </div>
                                    {doctor.email && (
                                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                        📧 {doctor.email}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleDoctorSelect(doctor, setFieldValue);
                                    }}
                                    style={{
                                      backgroundColor: '#009b94',
                                      color: '#fff',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap',
                                      marginLeft: '10px',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#007d75';
                                      e.target.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#009b94';
                                      e.target.style.transform = 'scale(1)';
                                    }}
                                  >
                                    + Add
                                  </button>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                              {searchTerm ? ' No doctors match your search' : ' No doctors available'}
                            </div>
                          )}
                        </div>

                        {/* PAGINATION INFO */}
                        <div style={{
                          padding: '12px',
                          borderTop: '1px solid #ddd',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '12px',
                        }}>
                          <div style={{ color: '#666' }}>
                            <strong>Showing {paginatedDoctors.length > 0 ? ((currentPage - 1) * doctorsPerPage) + 1 : 0}-{Math.min(currentPage * doctorsPerPage, totalAvailable)}</strong> of <strong>{totalAvailable}</strong> available doctors
                            {searchTerm && <span style={{ color: '#999' }}> (filtered from {doctorsList.length} total)</span>}
                          </div>
                          <div style={{ color: '#666' }}>
                            <strong>Page {currentPage} of {totalPages}</strong>
                          </div>
                        </div>

                        {/* PAGINATION CONTROLS */}
                        {totalPages > 1 && (
                          <div style={{
                            padding: '10px 12px',
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderTop: '1px solid #ddd',
                            flexWrap: 'wrap',
                            backgroundColor: '#fff'
                          }}>
                            <button
                              type="button"
                              onClick={handlePrevPage}
                              disabled={currentPage === 1}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: currentPage === 1 ? '#e0e0e0' : '#009b94',
                                color: currentPage === 1 ? '#999' : '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage > 1) {
                                  e.target.style.backgroundColor = '#007d75';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage > 1) {
                                  e.target.style.backgroundColor = '#009b94';
                                }
                              }}
                            >
                              ← Prev
                            </button>

                            {/* PAGE NUMBERS */}
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = idx + 1;
                                } else if (currentPage <= 3) {
                                  pageNum = idx + 1;
                                } else if (currentPage >= totalPages - 2) {
                                  pageNum = totalPages - (4 - idx);
                                } else {
                                  pageNum = currentPage - 2 + idx;
                                }

                                return (
                                  <button
                                    key={pageNum}
                                    type="button"
                                    onClick={() => {
                                      setCurrentPage(pageNum);
                                    }}
                                    style={{
                                      padding: '6px 10px',
                                      backgroundColor: currentPage === pageNum ? '#009b94' : '#f0f0f0',
                                      color: currentPage === pageNum ? '#fff' : '#333',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: currentPage === pageNum ? '700' : '500',
                                      fontSize: '12px',
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (currentPage !== pageNum) {
                                        e.target.style.backgroundColor = '#d0d0d0';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (currentPage !== pageNum) {
                                        e.target.style.backgroundColor = '#f0f0f0';
                                      }
                                    }}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>

                            <button
                              type="button"
                              onClick={() => handleNextPage(totalPages)}
                              disabled={currentPage === totalPages}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: currentPage === totalPages ? '#e0e0e0' : '#009b94',
                                color: currentPage === totalPages ? '#999' : '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage < totalPages) {
                                  e.target.style.backgroundColor = '#007d75';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage < totalPages) {
                                  e.target.style.backgroundColor = '#009b94';
                                }
                              }}
                            >
                              Next →
                            </button>
                          </div>
                        )}

                        {/* LOAD MORE BUTTON */}
                        {!isLoadingMore && hasMoreDoctors && (
                          <div style={{
                            padding: '12px',
                            borderTop: '1px solid #ddd',
                            backgroundColor: '#f0f8f7',
                            textAlign: 'center'
                          }}>
                            <button
                              type="button"
                              onClick={loadMoreDoctors}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: '#009b94',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#007d75';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#009b94';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                               Load More Doctors
                            </button>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                              Load more doctors from database
                            </div>
                          </div>
                        )}

                        {isLoadingMore && (
                          <div style={{
                            padding: '12px',
                            borderTop: '1px solid #ddd',
                            backgroundColor: '#f0f8f7',
                            textAlign: 'center',
                            color: '#009b94',
                            fontSize: '13px'
                          }}>
                            <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />
                            Loading more doctors...
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* NO DOCTORS MESSAGE */}
                {!loadingDoctors && doctorsList.length === 0 && (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404'
                  }}>
                    <strong>⚠️ No doctors loaded</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>Verify doctors exist in the database</p>
                  </div>
                )}

                
                {selectedDoctors.length > 0 && (
                  <div className="selected-doctors mt-3">
                    <h6>Selected Doctors:</h6>
                    {selectedDoctors.map((doctor, index) => (
                      <div key={doctor.doctor_id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                        <div>
                          <strong>{doctor.doctor_name}</strong> - {doctor.doctor_specialization}
                          {doctor.rating && <span className="ms-2">⭐ {doctor.rating}</span>}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveDoctor(doctor.doctor_id, setFieldValue)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <ErrorMessage name="doctors" component="div" className="error-message" />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  type="text"
                  name="clinic_name"
                  placeholder="Clinic Name"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage
                  name="clinic_name"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  type="text"
                  name="city"
                  placeholder="City"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage
                  name="city"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-12 mb-4">
                <Field
                  type="text"
                  name="clinic_address"
                  placeholder="Clinic Address"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage
                  name="clinic_address"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  type="text"
                  name="duration"
                  placeholder="Duration (e.g., 2 hours)"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage
                  name="duration"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  type="number"
                  name="pricing"
                  placeholder="Pricing"
                  className="form-control border-0 border-bottom"
                />
                <ErrorMessage
                  name="pricing"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-md-6 mb-4">
                <Field
                  as="select"
                  name="currency"
                  className="form-control border-0 border-bottom"
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Field>
                <ErrorMessage
                  name="currency"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn"
                  style={{
                    border: "1px solid #79747E",
                    color: "#333",
                    background: "none",
                  }}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{
                    background: "#009B93",
                    color: "#fff",
                  }}
                  disabled={isLoading}
                >
                  {isLoading && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  )}{" "}
                  Add Surgery
                </button>
              </div>
            </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
      <Toast
        cName={showToast}
        icon={ToastIcon}
        color={iconColor}
        text={ToastMessage}
      />
      <style>{`
        .doctor-list-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .doctor-list-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .doctor-list-scroll::-webkit-scrollbar-thumb {
          background: #009b94;
          border-radius: 4px;
        }
        .doctor-list-scroll::-webkit-scrollbar-thumb:hover {
          background: #007d75;
        }
      `}</style>
    </Fragment>
  );
}
